/**
 * Session Health Monitor Service
 * 
 * Monitors session health and proactively detects issues.
 * Triggers token refresh before expiration and monitors connection health.
 * 
 * Issue: #202
 */

import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs'
import { map, distinctUntilChanged } from 'rxjs/operators'

import { AuthenticationService } from '@/services/authentication.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { TokenRefreshOrchestrator, RefreshSource } from '@/services/token-refresh-orchestrator.service'
import { log, warn, error } from '@/util/logger'

export interface HealthMetrics {
  lastSuccessfulRequest: number | null
  lastTokenRefresh: number | null
  consecutiveFailures: number
  websocketConnected: boolean
  tokenExpiresAt: number | null
  lastFailure?: number
  sessionStartTime: number
}

/**
 * Service for monitoring session health and proactively managing token refresh.
 */
@Injectable({ providedIn: 'root' })
export class SessionHealthMonitor implements OnDestroy {
  private _healthCheckInterval = 5 * 60 * 1000 // 5 minutes
  private _tokenExpirationWarningThreshold = 10 * 60 * 1000 // 10 minutes
  private _healthMetrics = new BehaviorSubject<HealthMetrics>({
    lastSuccessfulRequest: null,
    lastTokenRefresh: null,
    consecutiveFailures: 0,
    websocketConnected: false,
    tokenExpiresAt: null,
    sessionStartTime: Date.now()
  })

  private _healthCheckSubscription: Subscription | null = null
  private _tokenUpdateSubscription: Subscription | null = null

  public readonly health$ = this._healthMetrics.asObservable()
  public readonly isHealthy$ = this.health$.pipe(
    map(metrics => this._calculateHealth(metrics)),
    distinctUntilChanged()
  )

  constructor(
    private _auth: AuthenticationService,
    private _sessionState: SessionStateService,
    private _refreshOrchestrator: TokenRefreshOrchestrator
  ) {
    // Start periodic health checks
    this._startHealthChecks()

    // Monitor token updates to track expiration
    this._tokenUpdateSubscription = this._auth.tokenUpdate$.subscribe(() => {
      this._updateTokenExpiration()
    })

    // Monitor session state changes
    this._sessionState.state$.subscribe(state => {
      if (state === SessionState.Authenticated) {
        this._updateMetrics(m => ({
          ...m,
          lastTokenRefresh: Date.now()
        }))
      }
    })
  }

  /**
   * Start periodic health checks
   */
  private _startHealthChecks(): void {
    this._healthCheckSubscription = interval(this._healthCheckInterval).subscribe(
      () => {
        this._performHealthCheck()
      }
    )

    // Perform initial health check
    this._performHealthCheck()
  }

  /**
   * Perform a health check
   */
  private _performHealthCheck(): void {
    const metrics = this._healthMetrics.value
    const now = Date.now()

    // Check if token is about to expire
    if (metrics.tokenExpiresAt) {
      const timeUntilExpiry = metrics.tokenExpiresAt - now

      if (
        timeUntilExpiry > 0 &&
        timeUntilExpiry < this._tokenExpirationWarningThreshold
      ) {
        // Token expires soon - proactively refresh
        log(
          `[SessionHealth] Token expires in ${Math.round(
            timeUntilExpiry / 1000 / 60
          )} minutes, proactively refreshing`
        )
        this._refreshOrchestrator.requestRefresh(
          RefreshSource.Proactive,
          `Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes`
        )
      } else if (timeUntilExpiry <= 0) {
        // Token already expired - only transition if not already expired
        const currentState = this._sessionState.currentState
        if (currentState !== SessionState.Expired && currentState !== SessionState.LoggedOut) {
          warn('[SessionHealth] Token has expired')
          this._sessionState.transitionTo(SessionState.Expired, {
            expiredAt: metrics.tokenExpiresAt
          })
        }
      }
    }

    // Check if we haven't had a successful request in a while
    if (metrics.lastSuccessfulRequest) {
      const timeSinceLastSuccess = now - metrics.lastSuccessfulRequest
      const warningThreshold = 15 * 60 * 1000 // 15 minutes

      if (timeSinceLastSuccess > warningThreshold) {
        warn(
          `[SessionHealth] No successful requests in ${Math.round(
            timeSinceLastSuccess / 1000 / 60
          )} minutes`
        )
      }
    }

    // Check consecutive failures
    if (metrics.consecutiveFailures >= 5) {
      error(
        `[SessionHealth] ${metrics.consecutiveFailures} consecutive failures detected`
      )
    }

    // Log health status
    const isHealthy = this._calculateHealth(metrics)
    if (!isHealthy) {
      warn('[SessionHealth] Session health check failed', metrics)
    }
  }

  /**
   * Update token expiration time from current token
   */
  private _updateTokenExpiration(): void {
    const currentUser = this._auth.currentUserValue
    if (!currentUser || !currentUser.token) {
      this._updateMetrics(m => ({
        ...m,
        tokenExpiresAt: null
      }))
      return
    }

    try {
      // Parse JWT to get expiration
      const parts = currentUser.token.split('.')
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]))
        if (payload.exp) {
          const expirationTime = payload.exp * 1000 // Convert to milliseconds
          this._updateMetrics(m => ({
            ...m,
            tokenExpiresAt: expirationTime
          }))
          log(
            `[SessionHealth] Token expires at:`,
            new Date(expirationTime).toISOString()
          )
        }
      }
    } catch (err) {
      warn('[SessionHealth] Failed to parse token expiration', err)
    }
  }

  /**
   * Record a successful request
   */
  recordSuccessfulRequest(): void {
    const now = Date.now()
    const metrics = this._healthMetrics.value
    const timeSinceLastSuccess = metrics.lastSuccessfulRequest 
      ? now - metrics.lastSuccessfulRequest 
      : null
    
    this._updateMetrics(m => ({
      ...m,
      lastSuccessfulRequest: now,
      consecutiveFailures: 0
    }))
    
    log('[SessionHealth] Recorded successful request', {
      timeSinceLastSuccess: timeSinceLastSuccess ? `${Math.round(timeSinceLastSuccess / 1000)}s` : 'first',
      consecutiveFailuresReset: true
    })
  }

  /**
   * Record a failed request
   */
  recordFailure(): void {
    const now = Date.now()
    const metrics = this._healthMetrics.value
    const newFailureCount = metrics.consecutiveFailures + 1
    
    this._updateMetrics(m => ({
      ...m,
      consecutiveFailures: newFailureCount,
      lastFailure: now
    }))
    
    warn('[SessionHealth] Recorded failure', {
      consecutiveFailures: newFailureCount,
      timeSinceLastSuccess: metrics.lastSuccessfulRequest 
        ? `${Math.round((now - metrics.lastSuccessfulRequest) / 1000)}s` 
        : 'never'
    })
  }

  /**
   * Update WebSocket connection status
   */
  updateWebSocketStatus(connected: boolean): void {
    const metrics = this._healthMetrics.value
    const wasConnected = metrics.websocketConnected
    
    this._updateMetrics(m => ({
      ...m,
      websocketConnected: connected
    }))
    
    if (wasConnected !== connected) {
      log('[SessionHealth] WebSocket status changed', {
        wasConnected,
        nowConnected: connected
      })
    }
  }

  /**
   * Calculate overall health status
   */
  private _calculateHealth(metrics: HealthMetrics): boolean {
    const now = Date.now()

    // Token must be valid
    const tokenValid =
      !metrics.tokenExpiresAt || metrics.tokenExpiresAt > now

    // Not too many consecutive failures
    const notTooManyFailures = metrics.consecutiveFailures < 5

    // Recent activity (successful request in last 30 minutes OR WebSocket connected)
    const recentActivity =
      metrics.lastSuccessfulRequest
        ? now - metrics.lastSuccessfulRequest < 30 * 60 * 1000
        : metrics.websocketConnected

    return tokenValid && notTooManyFailures && recentActivity
  }

  /**
   * Update health metrics
   */
  private _updateMetrics(
    updater: (metrics: HealthMetrics) => HealthMetrics
  ): void {
    this._healthMetrics.next(updater(this._healthMetrics.value))
  }

  /**
   * Get current health metrics
   */
  get currentMetrics(): HealthMetrics {
    return this._healthMetrics.value
  }

  ngOnDestroy(): void {
    if (this._healthCheckSubscription) {
      this._healthCheckSubscription.unsubscribe()
    }
    if (this._tokenUpdateSubscription) {
      this._tokenUpdateSubscription.unsubscribe()
    }
  }
}
